import { useCallback, useState } from 'react';

/**
 * Per-user dashboard card visibility, persisted in localStorage (Phase 3 v1).
 * Keyed globally (not per-brand) — the card SET is the same across brands.
 * A future v2 can move this to the User record via PATCH /users/me; the hook
 * API (hidden set + toggle) would stay the same.
 */
const STORAGE_KEY = 'traaker.dashboardLayout.hidden.v1';

export type DashboardCardId =
  | 'geo'
  | 'tasks'
  | 'competitors'
  | 'engines'
  | 'pages'
  | 'zones'
  | 'languages'
  | 'targets'
  | 'comparison';

/** Pure helpers (exported for unit tests — no React, no DOM beyond localStorage). */
export function readHiddenSet(): Set<DashboardCardId> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr as DashboardCardId[]) : new Set();
  } catch {
    return new Set();
  }
}

/** Returns the next hidden set after toggling `id`, persisting it to storage. */
export function toggleHidden(prev: Set<DashboardCardId>, id: DashboardCardId): Set<DashboardCardId> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // localStorage unavailable (private mode) — keep in-memory only.
  }
  return next;
}

export function useDashboardLayout() {
  const [hidden, setHidden] = useState<Set<DashboardCardId>>(() => readHiddenSet());

  const toggle = useCallback((id: DashboardCardId) => {
    setHidden((prev) => toggleHidden(prev, id));
  }, []);

  const isVisible = useCallback((id: DashboardCardId) => !hidden.has(id), [hidden]);

  return { hidden, toggle, isVisible };
}
